/**
 * Mobile tab bar — active state sync, chat/cart drawers, backdrop (non-modal drawers).
 */
(function () {
  const TAB_BAR_MQ = window.matchMedia('(max-width: 749px)');
  const RESERVE_DRAWER_SELECTOR = 'theme-drawer[data-uucck-reserve-tab-bar][open]';
  const ACTIVE_CLASS = 'uucck-mobile-tab-bar__item--active';

  function getBar() {
    return document.querySelector('.uucck-mobile-tab-bar');
  }

  const TAB_BAR_SCROLL_BUFFER_PX = 12;
  const TAB_BAR_PANEL_EXTRA_PX = 12;
  const CATALOG_PANEL_SELECTOR =
    '.template-index .section-resource-list, .template-collection .product-grid-container.section';

  /**
   * Fixed tab bar overlaps the scrollport — measure painted height and reserve inset.
   * Industry pattern: scroll-padding-bottom + content padding (MDN scroll-padding / env(safe-area)),
   * with JS only to read the real fixed bar height (not per-device breakpoints).
   */
  function syncTabBarLayoutReserve() {
    const bar = getBar();
    const root = document.documentElement;
    const body = document.body;
    const wrapper = document.querySelector('.page-wrapper');
    const main = document.querySelector('#MainContent');

    function clearReserve() {
      root.style.removeProperty('--uucck-tab-bar-height');
      root.style.removeProperty('--uucck-tab-bar-scroll-inset');
      root.style.removeProperty('scroll-padding-bottom');
      body?.style.removeProperty('scroll-padding-bottom');
      wrapper?.style.removeProperty('padding-bottom');
      main?.style.removeProperty('padding-bottom');
      root.removeAttribute('data-uucck-tab-inset');
      document.querySelectorAll(CATALOG_PANEL_SELECTOR).forEach((el) => {
        el.style.removeProperty('padding-bottom');
      });
    }

    if (!TAB_BAR_MQ.matches || !bar) {
      clearReserve();
      return;
    }

    const height = Math.ceil(bar.getBoundingClientRect().height) || 52;
    const shortViewportExtra = window.innerHeight < 720 ? 8 : 0;
    const inset = height + TAB_BAR_SCROLL_BUFFER_PX + shortViewportExtra;
    const panelPad = TAB_BAR_PANEL_EXTRA_PX + inset;

    root.style.setProperty('--uucck-tab-bar-height', `${height}px`);
    root.style.setProperty('--uucck-tab-bar-scroll-inset', `${inset}px`);
    root.style.setProperty('scroll-padding-bottom', `${inset}px`);
    body?.style.setProperty('scroll-padding-bottom', `${inset}px`);
    root.dataset.uucckTabInset = String(inset);

    wrapper?.style.setProperty('padding-bottom', `${inset}px`);
    main?.style.setProperty('padding-bottom', `${inset}px`);

    document.querySelectorAll(CATALOG_PANEL_SELECTOR).forEach((el) => {
      el.style.setProperty('padding-bottom', `${panelPad}px`);
    });

    nudgeHcaptchaBadge();
  }

  window.uucckSyncTabBarLayoutReserve = syncTabBarLayoutReserve;

  function normalizePath(pathname) {
    const path = pathname.replace(/\/+$/, '');
    return path === '' ? '/' : path;
  }

  function resolveTabFromLocation() {
    const path = normalizePath(location.pathname);

    if (path === '/') return 'home';
    if (path === '/cart') return 'cart';
    if (path === '/collections' || path.startsWith('/collections/')) return 'products';

    const accountPrefixes = ['/account', '/challenge'];
    if (accountPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
      return 'account';
    }

    if (path === '/pages/chat') return 'chat';

    return '';
  }

  function resolveTabWithDrawers() {
    if (document.querySelector('#cart-drawer[open]')) return 'cart';
    return resolveTabFromLocation();
  }

  function setActiveTab(tabKey) {
    const bar = getBar();
    if (!bar) return;

    const key = tabKey || bar.dataset.uucckActiveTab || resolveTabWithDrawers();

    bar.querySelectorAll('[data-uucck-tab]').forEach((el) => {
      const isActive = key !== '' && el.dataset.uucckTab === key;
      el.classList.toggle(ACTIVE_CLASS, isActive);
      if (isActive) {
        el.setAttribute('aria-current', 'page');
      } else {
        el.removeAttribute('aria-current');
      }
    });

    if (key) {
      bar.dataset.uucckActiveTab = key;
    } else {
      bar.removeAttribute('data-uucck-active-tab');
    }
  }

  function syncActiveTab() {
    setActiveTab(resolveTabWithDrawers());
  }

  function getBackdrop() {
    let el = document.getElementById('uucck-drawer-backdrop');
    if (!el) {
      el = document.createElement('button');
      el.id = 'uucck-drawer-backdrop';
      el.type = 'button';
      el.className = 'uucck-drawer-backdrop';
      el.hidden = true;
      el.setAttribute('aria-label', 'Close');
      document.body.appendChild(el);
      el.addEventListener('click', () => {
        const openDrawer = document.querySelector(RESERVE_DRAWER_SELECTOR);
        if (openDrawer && typeof openDrawer.close === 'function') {
          openDrawer.close();
        } else {
          el.hidden = true;
        }
      });
    }
    return el;
  }

  function syncBackdrop() {
    const backdrop = getBackdrop();
    const useBackdrop = TAB_BAR_MQ.matches && getBar();
    const openReserveDrawer = document.querySelector(RESERVE_DRAWER_SELECTOR);
    backdrop.hidden = !(useBackdrop && openReserveDrawer);
    syncActiveTab();
  }

  function closeReserveDrawers() {
    document.querySelectorAll(RESERVE_DRAWER_SELECTOR).forEach((drawer) => {
      if (typeof drawer.close === 'function') {
        drawer.close();
      }
    });
    syncBackdrop();
  }

  function anchorMobileTabAccountDialog(accountEl) {
    if (!TAB_BAR_MQ.matches || !(accountEl instanceof HTMLElement)) return;

    const tabRect = accountEl.getBoundingClientRect();
    const gapAboveTab = 10;
    const topInset = 12;
    const available = Math.max(240, tabRect.top - topInset - gapAboveTab);
    const maxHeight = Math.min(560, available);
    const dialogTop = Math.max(topInset, Math.round(tabRect.top - maxHeight - gapAboveTab));

    accountEl.style.setProperty('--uucck-account-dialog-top', `${dialogTop}px`);
    accountEl.style.setProperty('--uucck-account-dialog-max-height', `${maxHeight}px`);
    accountEl.style.setProperty('--shopify-account-dialog-position-top', `${dialogTop}px`);
  }

  function anchorSignInPageAccountDialog(accountEl) {
    if (!(accountEl instanceof HTMLElement)) return;
    const tabBar = getBar();
    const tabHeight = tabBar
      ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--uucck-tab-bar-height')) || 52
      : 52;
    const header = document.querySelector('#header-group');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 56;
    const top = Math.max(12, Math.round(headerBottom + 8));
    const maxHeight = Math.max(320, window.innerHeight - top - tabHeight - 16);
    accountEl.style.setProperty('--shopify-account-dialog-position-top', `${top}px`);
    accountEl.style.setProperty('--uucck-account-dialog-max-height', `${maxHeight}px`);
  }

  function bindBarInteractions() {
    const bar = getBar();
    if (!bar || bar.dataset.uucckTabBound === 'true') return;
    bar.dataset.uucckTabBound = 'true';

    bar.addEventListener('click', (event) => {
      const item = event.target.closest('[data-uucck-tab]');
      if (!item || !bar.contains(item)) return;
      const tab = item.dataset.uucckTab;
      if (tab) setActiveTab(tab);
    });

    bar.querySelectorAll('[data-uucck-tab-account-link]').forEach((link) => {
      link.addEventListener('click', () => {
        closeReserveDrawers();
        setActiveTab('account');
      });
    });

    const accountItem = bar.querySelector('[data-uucck-tab="account"].uucck-mobile-tab-bar__item--has-native');
    accountItem?.addEventListener(
      'pointerdown',
      () => {
        closeReserveDrawers();
        setActiveTab('account');
      },
      true
    );
  }

  document.addEventListener(
    'open',
    (event) => {
      const { target } = event;
      if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

      if (target.classList.contains('uucck-mobile-tab-bar__account-native')) {
        closeReserveDrawers();
        anchorMobileTabAccountDialog(target);
        return;
      }

      if (target.classList.contains('uucck-sign-in-page__account')) {
        anchorSignInPageAccountDialog(target);
      }
    },
    true
  );

  document.addEventListener('theme-drawer:open', (event) => {
    syncBackdrop();
    const id = event.target?.id;
    if (id === 'cart-drawer') setActiveTab('cart');
  });

  document.addEventListener('theme-drawer:close', syncBackdrop);
  TAB_BAR_MQ.addEventListener('change', syncBackdrop);

  window.addEventListener('pageshow', syncActiveTab);
  window.addEventListener('popstate', syncActiveTab);
  function observePageTemplateChanges() {
    const main = document.querySelector('#MainContent');
    if (!main || main.dataset.uucckTabObserve === 'true') return;
    main.dataset.uucckTabObserve = 'true';
    new MutationObserver(() => syncActiveTab()).observe(main, {
      attributes: true,
      attributeFilter: ['data-template'],
    });
    new MutationObserver(() => syncActiveTab()).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindBarInteractions();
    observePageTemplateChanges();
    syncTabBarLayoutReserve();
    syncActiveTab();
  });

  if (document.readyState !== 'loading') {
    bindBarInteractions();
    observePageTemplateChanges();
    syncTabBarLayoutReserve();
    syncActiveTab();
  }

  window.addEventListener('pageshow', () => {
    syncTabBarLayoutReserve();
    syncActiveTab();
  });
  window.addEventListener('resize', syncTabBarLayoutReserve);
  window.addEventListener('orientationchange', () => {
    window.setTimeout(syncTabBarLayoutReserve, 100);
  });
  TAB_BAR_MQ.addEventListener('change', syncTabBarLayoutReserve);
  window.visualViewport?.addEventListener('resize', syncTabBarLayoutReserve);
  document.addEventListener('uucck:collection-listing-updated', syncTabBarLayoutReserve);
  if (typeof ResizeObserver !== 'undefined') {
    const tabBarResizeObserver = new ResizeObserver(() => syncTabBarLayoutReserve());
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        const bar = getBar();
        if (bar) tabBarResizeObserver.observe(bar);
      },
      { once: true }
    );
    if (document.readyState !== 'loading') {
      const bar = getBar();
      if (bar) tabBarResizeObserver.observe(bar);
    }
  }

  /** Keep Shopify hCaptcha badge above the fixed mobile tab bar (protection unchanged). */
  function nudgeHcaptchaBadge() {
    if (!TAB_BAR_MQ.matches || !getBar()) return;

    const root = document.documentElement;
    const tabHeight =
      getComputedStyle(root).getPropertyValue('--uucck-tab-bar-height').trim() || '52px';
    const bottom = `calc(${tabHeight} + 6px)`;
    const zIndex = '26';

    const candidates = document.querySelectorAll(
      '.hcaptcha-badge, iframe[src*="hcaptcha.com"], div[id*="hcaptcha"]'
    );

    candidates.forEach((el) => {
      const host =
        el.closest('div[style*="position: fixed"]') ||
        el.closest('div[style*="position:fixed"]') ||
        el.parentElement;
      const target = host instanceof HTMLElement ? host : el;
      if (!(target instanceof HTMLElement)) return;
      target.style.setProperty('bottom', bottom, 'important');
      target.style.setProperty('z-index', zIndex, 'important');
    });
  }

  if (!document.body.dataset.uucckHcaptchaNudge) {
    document.body.dataset.uucckHcaptchaNudge = 'true';
    nudgeHcaptchaBadge();
    new MutationObserver(nudgeHcaptchaBadge).observe(document.body, {
      childList: true,
      subtree: true,
    });
    TAB_BAR_MQ.addEventListener('change', nudgeHcaptchaBadge);
  }

})();
